import type { SveaskogImportResult } from '@wehunt/ui'
import { apiFetch } from '../../../shared/api/apiFetch'
import { ApiError } from '../../../shared/api/ApiError'

/**
 * Import a hunting-ground boundary from Sveaskog (the Swedish state forestry
 * company) by the property's objektnummer (CLUB-189). The backend proxies Tanger to
 * resolve the official boundary, stores it, and writes the hunting ground.
 *
 * Returns a {@link SveaskogImportResult}:
 *  - `created` / `refreshed` — imported (or an existing area's boundary refreshed)
 *  - `not-found`             — no property matches the objektnummer (HTTP 404)
 *  - `error` + `code`        — any other failure, carrying the backend's stable
 *                              error code (e.g. `sveaskog.not_entitled`) so the
 *                              UI can show it. Non-API (network) errors rethrow.
 */
interface ImportResponse {
  id: string
  status: 'created' | 'refreshed'
}

export async function importSveaskogArea(
  groupId: string,
  objektnummer: string,
  token: string,
): Promise<SveaskogImportResult> {
  try {
    const res = await apiFetch<ImportResponse>(
      `/clubhouse/groups/${encodeURIComponent(groupId)}/hunting-grounds/import/sveaskog`,
      {
        method: 'POST',
        body: JSON.stringify({ objektnummer }),
        token,
      },
    )
    return { status: res.status }
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.status === 404) return { status: 'not-found' }
      const code = typeof err.problem.code === 'string' ? err.problem.code : null
      return { status: 'error', code }
    }
    throw err
  }
}
